import type React from "react";
import { useTranslation } from "react-i18next";
import type { DashboardReading } from "@/api/districts/dashboard-types";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import i18n from "@/i18n";
import { formatDateTime } from "@/utils/format-datetime";

interface DashboardReadingsStatusCardProps {
	nextReadingDate?: string | null;
	lastReading?: DashboardReading | null;
	isLoading?: boolean;
	extraInfo?: { label: string; value: React.ReactNode }[];
}

export function DashboardReadingsStatusCard({
	nextReadingDate,
	lastReading,
	isLoading = false,
	extraInfo,
}: DashboardReadingsStatusCardProps) {
	const { t } = useTranslation();
	const lang = i18n.language as "pl" | "en";

	const formattedLast = lastReading
		? formatDateTime(lastReading.reading_date, lang)
		: undefined;
	const formattedNext = formatDateTime(nextReadingDate, lang);
	const showLastReading = isLoading || !!lastReading;
	const showTiles = isLoading || !!lastReading;

	const tiles = [
		{
			key: "completed",
			value: lastReading?.cnt_completed,
			dotClass: "bg-emerald-500",
			label: t("mqttReadingsTable.cntCompleted"),
			tooltip: t("mqttReadingsTable.cntCompletedTooltip"),
		},
		{
			key: "error",
			value: lastReading?.cnt_error,
			dotClass: "bg-red-500",
			label: t("mqttReadingsTable.cntError"),
			tooltip: t("mqttReadingsTable.cntErrorTooltip"),
		},
		{
			key: "no-comm",
			value: lastReading?.cnt_no_communication,
			dotClass: "bg-amber-500",
			label: t("mqttReadingsTable.cntNoCommunication"),
			tooltip: t("mqttReadingsTable.cntNoCommunicationTooltip"),
		},
		{
			key: "request",
			value: lastReading?.cnt_request_sent,
			dotClass: "bg-blue-400",
			label: t("mqttReadingsTable.cntRequestSent"),
			tooltip: t("mqttReadingsTable.cntRequestSentTooltip"),
		},
	];

	return (
		<Card className="bg-muted/30">
			<CardContent className="flex flex-col gap-4 p-6">
				<div className="flex flex-col gap-2">
					{showLastReading && (
						<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
							<div className="flex items-baseline gap-2">
								<span className="text-muted-foreground text-xs">
									{t("dashboard.cards.lastReadingStatus")}:
								</span>
								{isLoading ? (
									<Skeleton className="h-5 w-32" />
								) : (
									<span className="text-foreground text-sm font-medium tabular-nums">
										{formattedLast ?? "—"}
									</span>
								)}
							</div>
							{!isLoading && lastReading && (
								<Badge variant={lastReading.completed ? "success" : "warning"}>
									{lastReading.completed
										? t("dashboard.cards.readingCompleted")
										: t("dashboard.cards.readingInProgress")}
								</Badge>
							)}
						</div>
					)}
					<div className="flex items-baseline gap-2">
						<span className="text-muted-foreground text-xs">
							{t("dashboard.cards.nextReading")}:
						</span>
						{isLoading ? (
							<Skeleton className="h-5 w-32" />
						) : (
							<span className="text-foreground text-sm font-medium tabular-nums">
								{formattedNext || "—"}
							</span>
						)}
					</div>
					{extraInfo?.map((info) => (
						<div className="flex items-baseline gap-2" key={info.label}>
							<span className="text-muted-foreground text-xs">
								{info.label}:
							</span>
							{isLoading ? (
								<Skeleton className="h-5 w-16" />
							) : (
								<span className="text-foreground text-sm font-medium tabular-nums">
									{info.value ?? "—"}
								</span>
							)}
						</div>
					))}
				</div>

				{showTiles && (
					<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
						{tiles.map((tile) => (
							<Tooltip key={tile.key}>
								<TooltipTrigger asChild>
									<div className="bg-card ring-foreground/10 hover:ring-foreground/25 flex cursor-help flex-col gap-1.5 rounded-lg p-3 ring-1 transition-shadow">
										<div className="flex items-center gap-2">
											<span
												className={`${tile.dotClass} size-2 rounded-full`}
												aria-hidden="true"
											/>
											<span className="text-muted-foreground text-xs">
												{tile.label}
											</span>
										</div>
										{isLoading ? (
											<Skeleton className="h-7 w-14" />
										) : (
											<span className="text-foreground text-2xl leading-tight font-semibold tabular-nums">
												{(tile.value ?? 0).toLocaleString(lang)}
											</span>
										)}
									</div>
								</TooltipTrigger>
								<TooltipContent side="top" className="max-w-60 text-xs">
									<span className="font-semibold">{tile.label}</span>:{" "}
									{tile.tooltip}
								</TooltipContent>
							</Tooltip>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}
